import React from "react";
import { useOcelMetadata } from "../../../stores/OcelContext";
import DateRangePresets from "../../filter/TabFilterModal/DateRangePresets";

const labelClass = "block text-sm font-medium text-gray-700 mb-1";
const selectClass =
  "w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white disabled:bg-gray-50 disabled:text-gray-400";
const hintClass = "text-xs text-gray-500 mt-1";

const OverdueValueTimelineSettings = ({
  activeFile,
  componentTitle,
  onComponentTitleChange,
  objectType,
  onObjectTypeChange,
  targetDateAttribute,
  onTargetDateAttributeChange,
  completionSourceType,
  onCompletionSourceTypeChange,
  completionSourceValue,
  onCompletionSourceValueChange,
  valueAttribute,
  onValueAttributeChange,
  allObjectAttributes,
  numericObjectAttributes,
  attrsLoading,
  viewMode,
  onViewModeChange,
  dateRangeStart,
  onDateRangeStartChange,
  dateRangeEnd,
  onDateRangeEndChange,
}) => {
  const metadata = useOcelMetadata(activeFile);
  const availableObjectTypes = metadata?.object_types
    ? Object.keys(metadata.object_types).sort()
    : [];

  // Activities from metadata — sorted
  const availableActivities = metadata?.event_types
    ? metadata.event_types.map((t) => t.name).sort()
    : metadata?.activity_types
    ? Object.keys(metadata.activity_types).sort()
    : [];

  const handleObjectTypeChange = (val) => {
    onObjectTypeChange(val);
    // Reset dependent fields when object type changes
    onTargetDateAttributeChange("");
    onCompletionSourceValueChange([]);
    onValueAttributeChange("");
  };

  const handleCompletionSourceTypeChange = (val) => {
    onCompletionSourceTypeChange(val);
    onCompletionSourceValueChange([]);
  };

  // Show all attrs for target date / completion object attr (may include date cols)
  // Fall back to metadata object_attribute_names if per-type fetch hasn't landed yet
  const objectAttrOptions =
    allObjectAttributes.length > 0
      ? allObjectAttributes
      : metadata?.object_attribute_names || [];

  const valueAttrOptions =
    numericObjectAttributes.length > 0
      ? numericObjectAttributes
      : objectAttrOptions; // fallback to all if numerics unknown

  const completionSourceOptions =
    completionSourceType === "object_attribute" ? objectAttrOptions : availableActivities;

  return (
    <div className="space-y-5">
      {/* 0 — Component Title */}
      <div>
        <label className={labelClass}>
          Component Title <span className="text-gray-400 font-normal">(optional)</span>
        </label>
        <input
          type="text"
          value={componentTitle || ""}
          onChange={(e) => onComponentTitleChange(e.target.value)}
          placeholder="Overdue Value Timeline"
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* 1 — Object Type */}
      <div>
        <label className={labelClass}>Object Type</label>
        <select
          value={objectType}
          onChange={(e) => handleObjectTypeChange(e.target.value)}
          disabled={availableObjectTypes.length === 0}
          className={selectClass}
        >
          <option value="">Select object type…</option>
          {availableObjectTypes.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <p className={hintClass}>Object type to analyse for overdue exposure</p>
      </div>

      {/* 2 — Target Date Attribute (Zieldatum) */}
      <div>
        <label className={labelClass}>Target Date <span className="text-gray-400 font-normal">(Zieldatum)</span></label>
        {attrsLoading ? (
          <p className="text-xs text-gray-400">Loading attributes…</p>
        ) : (
          <select
            value={targetDateAttribute}
            onChange={(e) => onTargetDateAttributeChange(e.target.value)}
            disabled={!objectType || objectAttrOptions.length === 0}
            className={selectClass}
          >
            <option value="">Select target date attribute…</option>
            {objectAttrOptions.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        )}
        <p className={hintClass}>Object attribute holding the planned/promised completion date</p>
      </div>

      {/* 3 — Completion Source */}
      <div>
        <label className={labelClass}>Completion Date Source <span className="text-gray-400 font-normal">(Abschlussdatum)</span></label>

        {/* Type toggle */}
        <div className="flex gap-2 mb-2">
          {[
            { id: "object_attribute", label: "Object Attribute" },
            { id: "activity", label: "Activity" },
          ].map(({ id, label }) => (
            <button
              key={id}
              type="button"
              onClick={() => handleCompletionSourceTypeChange(id)}
              className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                completionSourceType === id
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Value multi-select (one or more sources, earliest per object wins) */}
        {attrsLoading && completionSourceType === "object_attribute" ? (
          <p className="text-xs text-gray-400">Loading attributes…</p>
        ) : (
          <div
            className="w-full border border-gray-300 rounded-md overflow-y-auto h-28"
            style={{
              opacity: !objectType || completionSourceOptions.length === 0 ? 0.5 : 1,
              pointerEvents: !objectType || completionSourceOptions.length === 0 ? "none" : "auto",
            }}
          >
            {completionSourceOptions.length === 0 && (
              <p className="px-3 py-2 text-sm text-gray-400">No options found</p>
            )}
            {completionSourceOptions.map((item) => (
              <div
                key={item}
                onClick={() => {
                  const next = completionSourceValue.includes(item)
                    ? completionSourceValue.filter((a) => a !== item)
                    : [...completionSourceValue, item];
                  onCompletionSourceValueChange(next);
                }}
                className={`px-3 py-1.5 cursor-pointer text-sm select-none ${
                  completionSourceValue.includes(item)
                    ? "bg-blue-600 text-white"
                    : "hover:bg-gray-100"
                }`}
              >
                {item}
              </div>
            ))}
          </div>
        )}
        <p className={hintClass}>
          {completionSourceType === "object_attribute"
            ? "One or more date attributes; the earliest resolved date is used as the completion timestamp."
            : "One or more activities; the earliest of their last-occurrence timestamps is used as the completion timestamp."}
        </p>
      </div>

      {/* 4 — Value Attribute (Wertattribut) */}
      <div>
        <label className={labelClass}>Value Attribute <span className="text-gray-400 font-normal">(Wertattribut)</span></label>
        {attrsLoading ? (
          <p className="text-xs text-gray-400">Loading attributes…</p>
        ) : (
          <select
            value={valueAttribute}
            onChange={(e) => onValueAttributeChange(e.target.value)}
            disabled={!objectType || valueAttrOptions.length === 0}
            className={selectClass}
          >
            <option value="">Select value attribute…</option>
            {valueAttrOptions.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        )}
        <p className={hintClass}>Numeric attribute to aggregate on the Y-axis (e.g. net_order_value)</p>
      </div>

      {/* 5 — Zeitraum */}
      <div>
        <label className={labelClass}>Zeitraum</label>
        <div className="flex gap-2 mb-2">
          {[
            { id: "granularity", label: "Gesamter Zeitraum" },
            { id: "daterange", label: "Benutzerdefinierter Zeitraum" },
          ].map(({ id, label }) => (
            <button
              key={id}
              type="button"
              onClick={() => onViewModeChange(id)}
              className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                viewMode === id
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {viewMode === "daterange" && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
                <input
                  type="date"
                  value={dateRangeStart || ""}
                  onChange={(e) => onDateRangeStartChange(e.target.value)}
                  className={selectClass}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
                <input
                  type="date"
                  value={dateRangeEnd || ""}
                  onChange={(e) => onDateRangeEndChange(e.target.value)}
                  className={selectClass}
                />
              </div>
            </div>
            <DateRangePresets
              value={{ startDate: dateRangeStart || null, endDate: dateRangeEnd || null, enabled: true }}
              onChange={(v) => {
                onDateRangeStartChange(v.startDate);
                onDateRangeEndChange(v.endDate);
              }}
            />
          </div>
        )}
        <p className={hintClass}>
          Granularität (Tag/Woche/Monat/Jahr) bleibt oben im Tile einstellbar.
        </p>
      </div>
    </div>
  );
};

export default OverdueValueTimelineSettings;
